"use client";

import { Formik, FieldArray, Form } from "formik";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { useEffect, useState, useCallback } from "react";
import { DatePickerWithRange } from "../date-rangepicker";
import useFlightBookingStore from "@/lib/useFlightBookingStore";
import SuggestionInput from "../search/SuggestionInput";
import { DateRange } from "react-day-picker";
import PassengerClass from "../search/PassengerClass";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { z } from "zod";
import { toFormikValidationSchema } from "zod-formik-adapter";
import FlightSearchForms from "./flight-search-form";
import { Button } from "../../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { cn } from "@/lib/utils";
import { Calendar } from "../../ui/calendar";
import { CalendarIcon, CircleMinus, Loader2 } from "lucide-react";
import { format } from "date-fns";
import dayjs from "dayjs";
import { toast } from "sonner";
import { toCapitalizeString } from "@/lib/internalApis";
import { useSearchHistory } from "@/hooks/useSearchHistory";
import { logFirebaseEvent } from "@/utils/firebaseAnalytics";

interface CityNameProps {
  fromCityOrAirport: string;
  toCityOrAirport: string;
  index?: number;
}

const PaxInfoSchema = z.object({
  ADULT: z.coerce
    .string()
    .min(1, "At least one adult is required")
    .max(9, "Maximum 9 adults allowed")
    .refine((val) => Number(val) >= 1, "At least one adult is required"),
  CHILD: z.coerce
    .string()
    .min(0, "Must be 0 or more")
    .max(9, "Maximum 9 children allowed")
    .refine((val) => Number(val) >= 0, "Cannot be negative"),
  INFANT: z.coerce
    .string()
    .min(0, "Must be 0 or more")
    .max(9, "Maximum 9 infants allowed")
    .refine((val) => Number(val) >= 0, "Cannot be negative")
    .superRefine((val, ctx) => {
      if (Number(val) > Number(ctx.path[0])) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Number of infants cannot exceed number of adults",
          path: ["INFANT"],
        });
      }
    }),
});

const RouteInfoSchema = z.object({
  fromCityOrAirport: z.object({
    code: z.string().min(1, "From city/airport code is required"),
  }),
  toCityOrAirport: z.object({
    code: z.string().min(1, "To city/airport code is required"),
  }),
  travelDate: z.string().min(1, "Travel date is required"),
});

const FormValuesSchema = z.object({
  cabinClass: z.enum(["ECONOMY", "PREMIUM_ECONOMY", "BUSINESS", "FIRST"]),
  paxInfo: PaxInfoSchema,
  routeInfos: z.array(RouteInfoSchema).min(1, "At least one route is required"),
  preferredAirline: z.array(
    z.object({
      code: z.string(),
    }),
  ),
  searchModifiers: z.object({
    isDirectFlight: z.boolean(),
    isConnectingFlight: z.boolean(),
    pft: z.enum(["REGULAR", "STUDENT", "SENIOR_CITIZEN"]),
  }),
});

type FormValues = z.infer<typeof FormValuesSchema>;

interface FlightSearchFormProps {
  onScroll?: (position: number) => void;
  onNavigate?: (url: string) => void;
}

const FlightSearchForm = ({ onScroll, onNavigate }: FlightSearchFormProps) => {
  const searchParams = useSearchParams();
  const { setRequestUrl } = useFlightBookingStore();
  const [tripType, setTripType] = useState<
    "oneway" | "roundtrip" | "multicity"
  >(
    (searchParams?.get("tripType") as "oneway" | "roundtrip" | "multicity") ||
      "oneway",
  );

  const [lastSearch, setLastSearch] = useState<any>(null);
  const [dateRanges, setDateRanges] = useState<(DateRange | undefined)[]>([
    undefined,
  ]);

  const [selectedCityNames, setSelectedCityNames] = useState<CityNameProps[]>(
    searchParams?.get("metadata")
      ? JSON.parse(searchParams?.get("metadata") as string)
      : [
          {
            fromCityOrAirport: "",
            toCityOrAirport: "",
            index: 0,
          },
        ],
  );
  // Move routeInfosLength state up
  const [routeInfosLength, setRouteInfosLength] = useState(
    searchParams.get("metadata")
      ? JSON.parse(searchParams.get("data") as string).routeInfos.length
      : 2,
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [datePopoverOpen, setDatePopoverOpen] = useState<boolean>(false);
  const [multiCityPopoverOpen, setMultiCityPopoverOpen] = useState<
    number | null
  >(null);

  const { data: searchHistory, isLoading: isLoadingHistory }: any =
    useSearchHistory();

  // Auto-flow states
  const [shouldOpenToSuggestion, setShouldOpenToSuggestion] =
    useState<boolean>(false);
  const [shouldOpenCalendar, setShouldOpenCalendar] = useState<boolean>(false);
  const [shouldOpenReturnCalendar, setShouldOpenReturnCalendar] =
    useState<boolean>(false);
  const [shouldOpenPassenger, setShouldOpenPassenger] =
    useState<boolean>(false);
  const [isHiddenButton, setIsHiddenButton] = useState<boolean>(true);

  // New separate flow states for oneway to roundtrip transition
  const [
    shouldOpenReturnCalendarOnTransition,
    setShouldOpenReturnCalendarOnTransition,
  ] = useState<boolean>(false);
  const [isTransitioningFromOneway, setIsTransitioningFromOneway] =
    useState<boolean>(false);

  // Multicity auto-flow states
  const [
    shouldOpenMulticityFromSuggestion,
    setShouldOpenMulticityFromSuggestion,
  ] = useState<number | null>(null);
  const [shouldOpenMulticityToSuggestion, setShouldOpenMulticityToSuggestion] =
    useState<number | null>(null);
  const [shouldOpenMulticityCalendar, setShouldOpenMulticityCalendar] =
    useState<number | null>(null);

  const router = useRouter();

  const initialValues: FormValues = {
    cabinClass: searchParams.get("data")
      ? JSON.parse(searchParams.get("data") as string).cabinClass
      : "ECONOMY",
    paxInfo: searchParams.get("data")
      ? JSON.parse(searchParams.get("data") as string).paxInfo
      : { ADULT: "1", CHILD: "0", INFANT: "0" },
    routeInfos: searchParams.get("data")
      ? JSON.parse(searchParams.get("data") as string).routeInfos
      : [
          {
            fromCityOrAirport: { code: "" },
            toCityOrAirport: { code: "" },
            travelDate: dayjs().format("YYYY-MM-DD"),
          },
        ],
    searchModifiers: {
      isDirectFlight: true,
      isConnectingFlight: searchParams.get("data")
        ? JSON.parse(searchParams.get("data") as string).searchModifiers
            .isConnectingFlight
        : true,
      pft: searchParams.get("data")
        ? JSON.parse(searchParams.get("data") as string).searchModifiers.pft
        : "REGULAR",
    },
    preferredAirline: searchParams?.get("data")
      ? JSON.parse(searchParams?.get("data") as string).preferredAirline
      : [],
  };

  // Move the useEffect logic to the top-level component
  useEffect(() => {
    const currentLength = initialValues.routeInfos.length;
    if (routeInfosLength !== currentLength) {
      setRouteInfosLength(currentLength);
    }
  }, [initialValues.routeInfos.length, routeInfosLength]);

  // Move the useEffect outside of Formik and update it to use routeInfosLength directly
  useEffect(() => {
    if (tripType === "multicity") {
      // Only update if the length is different to avoid clearing existing values
      if (selectedCityNames.length !== routeInfosLength) {
        setSelectedCityNames(
          Array(routeInfosLength)
            .fill(0)
            .map((_, index) => ({
              fromCityOrAirport:
                selectedCityNames[index]?.fromCityOrAirport || "",
              toCityOrAirport: selectedCityNames[index]?.toCityOrAirport || "",
              index,
            })),
        );
      }
    }
  }, [tripType, routeInfosLength]);

  const handleTripTypeChange = (
    value: "oneway" | "roundtrip" | "multicity",
    setFieldValue: (field: string, value: any) => void,
    values: FormValues,
    newDateRange?: any,
  ) => {
    setTripType(value);
    if (value === "roundtrip") {
      // For roundtrip, create return journey with swapped cities
      const returnRoute = {
        fromCityOrAirport: { ...values.routeInfos[0].toCityOrAirport },
        toCityOrAirport: { ...values.routeInfos[0].fromCityOrAirport },
        travelDate: newDateRange?.to
          ? dayjs(newDateRange.to).format("YYYY-MM-DD")
          : dayjs(values.routeInfos[0]?.travelDate || new Date()).format(
              "YYYY-MM-DD",
            ) || "",
      };

      setFieldValue("routeInfos", [
        {
          ...values.routeInfos[0],
          travelDate: newDateRange?.from
            ? dayjs(newDateRange.from).format("YYYY-MM-DD")
            : values.routeInfos[0]?.travelDate || "",
        },
        returnRoute,
      ]);

      // Update selectedCityNames for roundtrip
      setSelectedCityNames([
        {
          fromCityOrAirport:
            selectedCityNames[0]?.fromCityOrAirport ||
            values.routeInfos[0].fromCityOrAirport.code,
          toCityOrAirport:
            selectedCityNames[0]?.toCityOrAirport ||
            values.routeInfos[0].toCityOrAirport.code,
          index: 0,
        },
        {
          fromCityOrAirport:
            selectedCityNames[0]?.toCityOrAirport ||
            values.routeInfos[0].toCityOrAirport.code,
          toCityOrAirport:
            selectedCityNames[0]?.fromCityOrAirport ||
            values.routeInfos[0].fromCityOrAirport.code,
          index: 1,
        },
      ]);
    } else if (value === "oneway") {
      // For oneway, keep only first route
      setFieldValue("routeInfos", [values.routeInfos[0]]);
      setDateRanges([undefined]);

      // Update selectedCityNames for oneway
      setSelectedCityNames([
        {
          fromCityOrAirport:
            selectedCityNames[0]?.fromCityOrAirport ||
            values.routeInfos[0].fromCityOrAirport.code,
          toCityOrAirport:
            selectedCityNames[0]?.toCityOrAirport ||
            values.routeInfos[0].toCityOrAirport.code,
          index: 0,
        },
      ]);
    } else if (value === "multicity") {
      // For multicity, ensure at least 2 routes
      const currentRoutes = [...values.routeInfos];
      while (currentRoutes.length < 2) {
        currentRoutes.push({
          fromCityOrAirport: { code: "" },
          toCityOrAirport: { code: "" },
          travelDate: "",
        });
      }
      setFieldValue("routeInfos", currentRoutes);
      setDateRanges(currentRoutes.map(() => undefined));
    }
  };

  // New function to handle oneway to roundtrip transition with auto-open return calendar
  const handleOnewayToRoundtripTransition = (
    setFieldValue: (field: string, value: any) => void,
    values: FormValues,
  ) => {
    // Set transition flag
    setIsTransitioningFromOneway(true);

    // Create return journey with swapped cities
    const returnRoute = {
      fromCityOrAirport: { ...values.routeInfos[0].toCityOrAirport },
      toCityOrAirport: { ...values.routeInfos[0].fromCityOrAirport },
      travelDate: dayjs(values.routeInfos[0]?.travelDate || new Date())
        .add(1, "day")
        .format("YYYY-MM-DD"),
    };

    setFieldValue("routeInfos", [
      {
        ...values.routeInfos[0],
        travelDate:
          values.routeInfos[0]?.travelDate || dayjs().format("YYYY-MM-DD"),
      },
      returnRoute,
    ]);

    // Update selectedCityNames for roundtrip
    setSelectedCityNames([
      {
        fromCityOrAirport:
          selectedCityNames[0]?.fromCityOrAirport ||
          values.routeInfos[0].fromCityOrAirport.code,
        toCityOrAirport:
          selectedCityNames[0]?.toCityOrAirport ||
          values.routeInfos[0].toCityOrAirport.code,
        index: 0,
      },
      {
        fromCityOrAirport:
          selectedCityNames[0]?.toCityOrAirport ||
          values.routeInfos[0].toCityOrAirport.code,
        toCityOrAirport:
          selectedCityNames[0]?.fromCityOrAirport ||
          values.routeInfos[0].fromCityOrAirport.code,
        index: 1,
      },
    ]);

    // Set trip type to roundtrip
    setTripType("roundtrip");

    // Schedule opening return calendar after state updates
    setTimeout(() => {
      setShouldOpenReturnCalendarOnTransition(true);
      setIsTransitioningFromOneway(false);
    }, 100);
  };

  const handleSubmit = (values: FormValues) => {
    setIsLoading(true);

    logFirebaseEvent("flight_search");

    try {
      // Validate same city check
      values.routeInfos.forEach((route, index) => {
        if (route.fromCityOrAirport.code === route.toCityOrAirport.code) {
          throw new Error(`Source and destination cannot be same`);
        }
      });

      // Rest of your submit logic
      const searchQuery = {
        ...values,
        routeInfos: values.routeInfos.filter(
          (route) =>
            route.fromCityOrAirport.code &&
            route.toCityOrAirport.code &&
            route.travelDate,
        ),
        searchModifiers: {
          ...values.searchModifiers,
          isDirectFlight: true,
        },
      };

      if (tripType === "roundtrip") {
        selectedCityNames[1] = {
          fromCityOrAirport: selectedCityNames[0].toCityOrAirport,
          toCityOrAirport: selectedCityNames[0].fromCityOrAirport,
          index: 1,
        };
      }
      if (searchQuery.routeInfos.length === 2 && tripType === "multicity") {
        if (
          searchQuery.routeInfos[0].fromCityOrAirport.code ===
            searchQuery.routeInfos[1].toCityOrAirport.code &&
          searchQuery.routeInfos[0].toCityOrAirport.code ===
            searchQuery.routeInfos[1].fromCityOrAirport.code
        ) {
          setTripType("roundtrip");
        }
      }

      const queryString = new URLSearchParams({
        data: JSON.stringify(searchQuery),
        metadata: JSON.stringify(selectedCityNames),
        tripType: tripType,
        _t: new Date().getTime().toString(), // Add a timestamp to force a refresh
      }).toString();

      setRequestUrl(`/flight-list?${queryString}`);

      // Use the navigation handler if provided, otherwise fallback to router.push
      if (onNavigate) {
        onNavigate(`/flight-list?${queryString}`);
      } else {
        window.scrollTo(0, 0);
        router.push(`/flight-list?${queryString}`);
      }
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const handleSwap = useCallback(
    (setFieldValue: any, values: FormValues, index: number = 0) => {
      const currentRoute = values.routeInfos[index];
      if (tripType === "roundtrip") {
        // Only swap the essential data
        setFieldValue(`routeInfos[0]`, {
          ...currentRoute,
          fromCityOrAirport: {
            code: currentRoute.toCityOrAirport.code,
          },
          toCityOrAirport: {
            code: currentRoute.fromCityOrAirport.code,
          },
        });
        setFieldValue(`routeInfos[1]`, {
          ...currentRoute,
          fromCityOrAirport: {
            code: currentRoute.fromCityOrAirport.code,
          },
          toCityOrAirport: {
            code: currentRoute.toCityOrAirport.code,
          },
        });
      }
      if (tripType === "oneway") {
        // Only swap the essential data
        setFieldValue(`routeInfos[${index}]`, {
          ...currentRoute,
          fromCityOrAirport: {
            code: currentRoute.toCityOrAirport.code,
          },
          toCityOrAirport: {
            code: currentRoute.fromCityOrAirport.code,
          },
        });
      }

      // Swap the display names
      setSelectedCityNames((prev) =>
        prev.map((city, i) =>
          i === index
            ? {
                fromCityOrAirport: prev[index].toCityOrAirport,
                toCityOrAirport: prev[index].fromCityOrAirport,
                index: i,
              }
            : city,
        ),
      );
    },
    [selectedCityNames],
  );

  // Add function to check if form is valid
  const isFormValid = (values: FormValues) => {
    // Check if any route has same source and destination
    const hasSameCity = values.routeInfos.some(
      (route) =>
        route.fromCityOrAirport.code &&
        route.toCityOrAirport.code &&
        route.fromCityOrAirport.code === route.toCityOrAirport.code,
    );

    // Check if all required fields are filled
    const hasAllFields = values.routeInfos.every(
      (route) =>
        route.fromCityOrAirport.code &&
        route.toCityOrAirport.code &&
        route.travelDate,
    );

    return !hasSameCity && hasAllFields;
  };

  const validateForm = (values: FormValues): boolean => {
    console.log(values);
    // First validate the basic required fields for the active trip type
    switch (tripType) {
      case "oneway":
        return (
          !!values.routeInfos[0]?.fromCityOrAirport?.code &&
          !!values.routeInfos[0]?.toCityOrAirport?.code &&
          !!values.routeInfos[0]?.travelDate
        );

      case "roundtrip":
        return (
          !!values.routeInfos[0]?.fromCityOrAirport?.code &&
          !!values.routeInfos[0]?.toCityOrAirport?.code &&
          !!values.routeInfos[0]?.travelDate &&
          !!values.routeInfos[1]?.travelDate
        );

      case "multicity":
        // For multicity, ensure at least 2 routes and all routes have required data
        if (values.routeInfos.length < 2) return false;

        return values.routeInfos.every(
          (route) =>
            !!route.fromCityOrAirport?.code &&
            !!route.toCityOrAirport?.code &&
            !!route.travelDate,
        );

      default:
        return false;
    }
  };

  // Function to update subsequent dates if they become invalid after a date change
  const updateSubsequentDates = (
    values: FormValues,
    setFieldValue: (field: string, value: any) => void,
    changedIndex: number,
    newDate: Date,
  ) => {
    // Only process if we're in multicity mode and there are subsequent routes
    if (
      tripType !== "multicity" ||
      changedIndex >= values.routeInfos.length - 1
    ) {
      return;
    }

    // Format the new date for comparison
    const newDateStr = dayjs(newDate).format("YYYY-MM-DD");

    // Track if any dates were updated
    let datesUpdated = false;

    // Check all subsequent routes
    for (let i = changedIndex + 1; i < values.routeInfos.length; i++) {
      const subsequentDateStr = values.routeInfos[i].travelDate;

      // If the subsequent date exists and is earlier than the new date, update it
      if (subsequentDateStr && subsequentDateStr < newDateStr) {
        // Set the subsequent date to the new date
        setFieldValue(`routeInfos[${i}].travelDate`, newDateStr);
        datesUpdated = true;
      }
    }
  };

  // Auto-flow callback handlers
  const handleFromSuggestionSelected = () => {
    // When "From" suggestion is selected, open "To" suggestion
    setShouldOpenToSuggestion(true);
  };

  const handleToSuggestionSelected = () => {
    // When "To" suggestion is selected, open calendar
    setShouldOpenCalendar(true);
    setShouldOpenToSuggestion(false);
  };

  const handleDepartureDateSelected = () => {
    // When departure date is selected
    setShouldOpenCalendar(false);
    if (tripType === "roundtrip") {
      // Open return calendar for roundtrip
      setShouldOpenReturnCalendar(true);
    } else {
      // Open passenger selector for oneway
      setShouldOpenPassenger(true);
    }
  };

  const handleReturnDateSelected = () => {
    // When return date is selected, open passenger selector
    setShouldOpenReturnCalendar(false);
    setShouldOpenPassenger(true);
  };

  // Helper function to get next field in continuous flow
  const getNextMulticityField = (
    currentIndex: number,
    currentField: "from" | "to" | "date",
    totalRoutes: number,
  ) => {
    // Current field progression: from → to → date
    if (currentField === "from") {
      return { index: currentIndex, field: "to" };
    } else if (currentField === "to") {
      return { index: currentIndex, field: "date" };
    } else if (currentField === "date") {
      // Move to next route's "from" field
      const nextIndex = currentIndex + 1;
      if (nextIndex < totalRoutes) {
        return { index: nextIndex, field: "from" };
      } else {
        // All routes completed, open passenger selector
        return { index: -1, field: "passenger" };
      }
    }
    return null;
  };

  // Multicity auto-flow callback handlers with continuous flow
  const handleMulticityFromSuggestionSelected = (
    index: number,
    values: FormValues,
  ) => {
    // Reset all auto-flow states first
    setShouldOpenMulticityFromSuggestion(null);
    setShouldOpenMulticityToSuggestion(null);
    setShouldOpenMulticityCalendar(null);

    // Get next field in continuous flow
    const nextField = getNextMulticityField(
      index,
      "from",
      values.routeInfos.length,
    );

    if (nextField?.field === "to") {
      setShouldOpenMulticityToSuggestion(nextField.index);
    }
  };

  const handleMulticityToSuggestionSelected = (
    index: number,
    values: FormValues,
  ) => {
    // Reset all auto-flow states first
    setShouldOpenMulticityFromSuggestion(null);
    setShouldOpenMulticityToSuggestion(null);
    setShouldOpenMulticityCalendar(null);

    // Get next field in continuous flow
    const nextField = getNextMulticityField(
      index,
      "to",
      values.routeInfos.length,
    );

    if (nextField?.field === "date") {
      setShouldOpenMulticityCalendar(nextField.index);
    }
  };

  const handleMulticityDateSelected = (index: number, values: FormValues) => {
    // Reset all auto-flow states first
    setShouldOpenMulticityFromSuggestion(null);
    setShouldOpenMulticityToSuggestion(null);
    setShouldOpenMulticityCalendar(null);

    // Get next field in continuous flow
    const nextField = getNextMulticityField(
      index,
      "date",
      values.routeInfos.length,
    );

    if (nextField?.field === "from") {
      setShouldOpenMulticityFromSuggestion(nextField.index);
    } else if (nextField?.field === "passenger") {
      // All routes completed, open passenger selector
      setShouldOpenPassenger(true);
    }
  };

  // Reset multicity auto-flow states when suggestion dropdown closes
  const resetMulticityAutoFlow = () => {
    setShouldOpenMulticityFromSuggestion(null);
    setShouldOpenMulticityToSuggestion(null);
    setShouldOpenMulticityCalendar(null);
  };

  // Start continuous multicity auto-flow from the first route
  const startMulticityAutoFlow = () => {
    // Reset all states first
    resetMulticityAutoFlow();
    // Start with the first route's "From" field
    setShouldOpenMulticityFromSuggestion(0);
  };

  // Auto-open effect for multicity calendars
  useEffect(() => {
    if (shouldOpenMulticityCalendar !== null) {
      setMultiCityPopoverOpen(shouldOpenMulticityCalendar);
    }
  }, [shouldOpenMulticityCalendar]);

  // Effect to handle oneway to roundtrip transition
  useEffect(() => {
    if (shouldOpenReturnCalendarOnTransition && tripType === "roundtrip") {
      // Reset the flag after a short delay to ensure the calendar opens
      setTimeout(() => {
        setShouldOpenReturnCalendarOnTransition(false);
      }, 50);
    }
  }, [shouldOpenReturnCalendarOnTransition, tripType]);

  return (
    <>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={toFormikValidationSchema(FormValuesSchema)}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {({ values, setFieldValue, errors, touched }) => {
          // Update routeInfosLength when values.routeInfos.length changes
          // Replace useEffect with direct update

          return (
            <Form className="flex flex-col gap-4 w-full">
              <div className="w-full">
                <Tabs
                  className="w-[100%]"
                  defaultValue={tripType}
                  value={tripType}
                >
                  <div className="bg-white p-7 rounded">
                    <div className="flex justify-start ">
                      <TabsList className="bg-white/5 backdrop-blur-md border border-white/10 h-10 flex-wrap">
                        <TabsTrigger
                          className="h-[100%] text-primary font-bold text-xs sm:text-[1rem] data-[state=active]:bg-primary data-[state=active]:text-white"
                          value="oneway"
                          onClick={() => {
                            handleTripTypeChange(
                              "oneway",
                              setFieldValue,
                              values,
                            );
                            setIsHiddenButton(true);
                          }}
                        >
                          One Way
                        </TabsTrigger>
                        <TabsTrigger
                          className="h-[100%] text-primary font-bold text-xs sm:text-[1rem] data-[state=active]:bg-primary data-[state=active]:text-white"
                          value="roundtrip"
                          onClick={() => {
                            handleTripTypeChange(
                              "roundtrip",
                              setFieldValue,
                              values,
                            );
                            setIsHiddenButton(true);
                          }}
                        >
                          Round Trip
                        </TabsTrigger>
                        <TabsTrigger
                          className="h-[100%] text-primary font-bold text-xs sm:text-[1rem] data-[state=active]:bg-primary data-[state=active]:text-white"
                          value="multicity"
                          onClick={() => {
                            handleTripTypeChange(
                              "multicity",
                              setFieldValue,
                              values,
                            );
                            // Start continuous auto-flow when multicity is selected
                            setTimeout(() => {
                              startMulticityAutoFlow();
                            }, 100);
                            setIsHiddenButton(true);
                          }}
                        >
                          Multi City
                        </TabsTrigger>
                      </TabsList>
                    </div>

                    <div className="flex items-start gap-4 lg:gap-2 mt-5 flex-wrap lg:flex-nowrap">
                      <TabsContent value="oneway" className="!mt-0 w-full">
                        <FieldArray name="oneway">
                          {() => (
                            <div className="flex flex-col lg:flex-row gap-2 w-full">
                              <div className="w-[100%] lg:w-[60%]">
                                <div className="flex flex-col lg:flex-row text-black relative">
                                  <div className="w-[100%] lg:w-[50%]">
                                    <SuggestionInput
                                      tripType={"oneway"}
                                      isFrom={true}
                                      index={0}
                                      selectedCityNames={selectedCityNames}
                                      setSelectedCityNames={
                                        setSelectedCityNames
                                      }
                                      onSwap={() =>
                                        handleSwap(setFieldValue, values)
                                      }
                                      onClick={() => onScroll?.(100)}
                                      onSuggestionSelected={
                                        handleFromSuggestionSelected
                                      }
                                    />
                                  </div>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSwap(setFieldValue, values);
                                    }}
                                    className={cn(
                                      "absolute top-[60px] lg:top-[30px] right-[46%] lg:right-[46%] transform -translate-y-1/2 z-10 rotate-90",
                                      "w-6 h-6 lg:w-10 lg:h-10 bg-white rounded-full border shadow-md",
                                      "flex items-center justify-center",
                                      "hover:bg-blue-50 transition-colors text-primary",
                                    )}
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="20"
                                      height="20"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    >
                                      <path d="M7 16V4M7 4L3 8M7 4L11 8M17 8V20M17 20L21 16M17 20L13 16" />
                                    </svg>
                                  </button>
                                  {/* <div className="col-span-1 lg:col-span-6"> */}
                                  <div className="w-[100%] lg:w-[50%] ">
                                    <SuggestionInput
                                      tripType={"oneway"}
                                      isFrom={false}
                                      index={0}
                                      selectedCityNames={selectedCityNames}
                                      setSelectedCityNames={
                                        setSelectedCityNames
                                      }
                                      onClick={() => onScroll?.(100)}
                                      onSuggestionSelected={
                                        handleToSuggestionSelected
                                      }
                                      shouldOpen={shouldOpenToSuggestion}
                                    />
                                  </div>
                                </div>
                              </div>
                              {/* <div className="col-span-1 lg:col-span-3"> */}
                              <div className="w-[100%] lg:w-[40%]">
                                <DatePickerWithRange
                                  titleFirst="departure date"
                                  titleSecond=" return date"
                                  className=""
                                  tripType={"oneway"}
                                  date={{
                                    from: values.routeInfos[0].travelDate
                                      ? new Date(
                                          values.routeInfos[0].travelDate,
                                        )
                                      : new Date(),
                                    to: values.routeInfos[0].travelDate
                                      ? new Date(
                                          values.routeInfos[0].travelDate,
                                        )
                                      : new Date(),
                                  }}
                                  setDate={(newDateRange: any) => {
                                    setDateRanges([newDateRange]);
                                    // Update form values when dates change
                                    if (newDateRange?.from) {
                                      setFieldValue(
                                        "routeInfos[0].travelDate",
                                        dayjs(newDateRange.from).format(
                                          "YYYY-MM-DD",
                                        ),
                                      );
                                    }
                                  }}
                                  onValueChange={(
                                    newDateRange: DateRange | undefined,
                                  ) => {
                                    if (newDateRange?.to) {
                                      handleTripTypeChange(
                                        "roundtrip",
                                        setFieldValue,
                                        values,
                                        newDateRange,
                                      );
                                    }
                                  }}
                                  onDepartureDateSelected={
                                    handleDepartureDateSelected
                                  }
                                  shouldOpenCalendar={shouldOpenCalendar}
                                  onOnewayToRoundtripTransition={() =>
                                    handleOnewayToRoundtripTransition(
                                      setFieldValue,
                                      values,
                                    )
                                  }
                                />
                              </div>
                            </div>
                          )}
                        </FieldArray>
                      </TabsContent>

                      <TabsContent value="roundtrip" className="!mt-0 w-full">
                        <FieldArray name="routeInfos">
                          {() => (
                            // <div className="grid grid-cols-1 lg:grid-cols-11 gap-2 w-[100%]">
                            <div className="flex flex-col lg:flex-row gap-2 w-[100%]">
                              <div className="w-[100%] lg:w-[60%] ">
                                {/* <div className="grid grid-cols-1 lg:grid-cols-12 text-black gap-2 relative w-full"> */}
                                <div className="flex flex-col lg:flex-row relative w-full">
                                  {/* <div className="col-span-1 lg:col-span-6 text-black w-full"> */}
                                  <div className="w-[100%] lg:w-[50%] text-black">
                                    <SuggestionInput
                                      tripType={"roundtrip"}
                                      isFrom={true}
                                      index={0}
                                      selectedCityNames={selectedCityNames}
                                      setSelectedCityNames={
                                        setSelectedCityNames
                                      }
                                      onSwap={() =>
                                        handleSwap(setFieldValue, values)
                                      }
                                      onClick={() => onScroll?.(100)}
                                      onSuggestionSelected={
                                        handleFromSuggestionSelected
                                      }
                                    />
                                  </div>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSwap(setFieldValue, values);
                                    }}
                                    className={cn(
                                      "absolute top-[60px] lg:top-[30px] right-[46%] lg:right-[46%] transform -translate-y-1/2 z-10 rotate-90",
                                      "w-6 h-6 lg:w-10 lg:h-10 bg-white rounded-full border shadow-md",
                                      "flex items-center justify-center",
                                      "hover:bg-blue-50 transition-colors text-primary",
                                    )}
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="20"
                                      height="20"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    >
                                      <path d="M7 16V4M7 4L3 8M7 4L11 8M17 8V20M17 20L21 16M17 20L13 16" />
                                    </svg>
                                  </button>
                                  {/* <div className="col-span-1 lg:col-span-6 text-black"> */}
                                  <div className="w-[100%] lg:w-[50%] text-black">
                                    <SuggestionInput
                                      tripType={"roundtrip"}
                                      isFrom={false}
                                      index={0}
                                      selectedCityNames={selectedCityNames}
                                      setSelectedCityNames={
                                        setSelectedCityNames
                                      }
                                      onClick={() => onScroll?.(100)}
                                      onSuggestionSelected={
                                        handleToSuggestionSelected
                                      }
                                      shouldOpen={shouldOpenToSuggestion}
                                    />
                                  </div>
                                </div>
                              </div>

                              {/* <div className="col-span-1 sm:col-span-4"> */}
                              <div className="w-[100%] lg:w-[40%]">
                                <DatePickerWithRange
                                  titleFirst="departure date"
                                  titleSecond=" return date"
                                  date={{
                                    from: values.routeInfos[0]?.travelDate
                                      ? new Date(
                                          values.routeInfos[0].travelDate,
                                        )
                                      : new Date(),
                                    to: values.routeInfos[1]?.travelDate
                                      ? new Date(
                                          values.routeInfos[1].travelDate,
                                        )
                                      : undefined,
                                  }}
                                  setDate={(newDateRange: any) => {
                                    setDateRanges([newDateRange]);
                                    // Update form values when dates change
                                    if (newDateRange?.from) {
                                      setFieldValue(
                                        "routeInfos[0].travelDate",
                                        dayjs(newDateRange.from).format(
                                          "YYYY-MM-DD",
                                        ),
                                      );
                                    }
                                    if (newDateRange?.to) {
                                      setFieldValue(
                                        "routeInfos[1].travelDate",
                                        dayjs(newDateRange.to).format(
                                          "YYYY-MM-DD",
                                        ),
                                      );
                                    }
                                  }}
                                  onValueChange={(
                                    newDateRange: DateRange | undefined,
                                  ) => {
                                    if (newDateRange?.to) {
                                      handleTripTypeChange(
                                        "roundtrip",
                                        setFieldValue,
                                        values,
                                        newDateRange,
                                      );
                                    }
                                  }}
                                  onDepartureDateSelected={
                                    handleDepartureDateSelected
                                  }
                                  onReturnDateSelected={
                                    handleReturnDateSelected
                                  }
                                  shouldOpenCalendar={shouldOpenCalendar}
                                  shouldOpenReturnCalendar={
                                    shouldOpenReturnCalendar ||
                                    shouldOpenReturnCalendarOnTransition
                                  }
                                />
                              </div>
                            </div>
                          )}
                        </FieldArray>
                      </TabsContent>

                      <TabsContent value="multicity" className="!mt-0 w-full">
                        <FieldArray name="routeInfos">
                          {({ push, remove }) => (
                            <>
                              {values.routeInfos.map((_, index) => (
                                <div
                                  key={index}
                                  className="grid lg:grid-cols-[4fr_4fr_4fr_auto] mb-4 gap-2"
                                >
                                  <SuggestionInput
                                    tripType={"multicity"}
                                    isFrom={true}
                                    index={index}
                                    selectedCityNames={selectedCityNames}
                                    setSelectedCityNames={setSelectedCityNames}
                                    onSwap={() =>
                                      handleSwap(setFieldValue, values, index)
                                    }
                                    onClick={() => onScroll?.(100)}
                                    onSuggestionSelected={() =>
                                      handleMulticityFromSuggestionSelected(
                                        index,
                                        values,
                                      )
                                    }
                                    shouldOpen={
                                      shouldOpenMulticityFromSuggestion ===
                                      index
                                    }
                                    onSuggestionClosed={resetMulticityAutoFlow}
                                  />
                                  <SuggestionInput
                                    tripType={"multicity"}
                                    isFrom={false}
                                    index={index}
                                    selectedCityNames={selectedCityNames}
                                    setSelectedCityNames={setSelectedCityNames}
                                    onClick={() => onScroll?.(100)}
                                    onSuggestionSelected={() =>
                                      handleMulticityToSuggestionSelected(
                                        index,
                                        values,
                                      )
                                    }
                                    shouldOpen={
                                      shouldOpenMulticityToSuggestion === index
                                    }
                                    onSuggestionClosed={resetMulticityAutoFlow}
                                  />
                                  <div className="lg:ml-2">
                                    <Popover
                                      key={index}
                                      open={multiCityPopoverOpen === index}
                                      onOpenChange={(open) =>
                                        setMultiCityPopoverOpen(
                                          open ? index : null,
                                        )
                                      }
                                    >
                                      <PopoverTrigger asChild>
                                        <Button
                                          variant={"outline"}
                                          className={cn(
                                            "w-[100%] !h-[60px] justify-start text-left font-normal",
                                            !values.routeInfos[index]
                                              .travelDate &&
                                              "text-muted-foreground",
                                          )}
                                        >
                                          {values.routeInfos[index]
                                            .travelDate ? (
                                            <p className="text-black">
                                              {format(
                                                values.routeInfos[index]
                                                  .travelDate,
                                                "PPP",
                                              )}
                                            </p>
                                          ) : (
                                            <span>Pick a date</span>
                                          )}
                                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                        </Button>
                                      </PopoverTrigger>
                                      <PopoverContent
                                        className="w-auto p-0 text-black"
                                        align="start"
                                        side="bottom"
                                      >
                                        <Calendar
                                          key={`calendar-${index}-${
                                            values.routeInfos[index]
                                              .travelDate || "new"
                                          }`}
                                          mode="single"
                                          selected={
                                            // For the selected date, use either existing date or previous route's date
                                            index > 0
                                              ? values.routeInfos[index]
                                                  .travelDate
                                                ? new Date(
                                                    values.routeInfos[index]
                                                      .travelDate,
                                                  )
                                                : values.routeInfos[index - 1]
                                                      .travelDate
                                                  ? new Date(
                                                      values.routeInfos[
                                                        index - 1
                                                      ].travelDate,
                                                    )
                                                  : new Date()
                                              : values.routeInfos[index]
                                                    .travelDate
                                                ? new Date(
                                                    values.routeInfos[index]
                                                      .travelDate,
                                                  )
                                                : new Date()
                                          }
                                          defaultMonth={
                                            // For default visible month, prioritize using:
                                            // 1. Current route's date if exists
                                            // 2. Previous route's date if exists
                                            // 3. Current date as fallback
                                            index > 0
                                              ? values.routeInfos[index]
                                                  .travelDate
                                                ? new Date(
                                                    values.routeInfos[index]
                                                      .travelDate,
                                                  )
                                                : values.routeInfos[index - 1]
                                                      .travelDate
                                                  ? new Date(
                                                      values.routeInfos[
                                                        index - 1
                                                      ].travelDate,
                                                    )
                                                  : new Date()
                                              : values.routeInfos[index]
                                                    .travelDate
                                                ? new Date(
                                                    values.routeInfos[index]
                                                      .travelDate,
                                                  )
                                                : new Date()
                                          }
                                          onSelect={(
                                            newDate: Date | undefined,
                                          ) => {
                                            // Always close the calendar first
                                            setMultiCityPopoverOpen(null);

                                            if (newDate) {
                                              const formattedDate =
                                                dayjs(newDate).format(
                                                  "YYYY-MM-DD",
                                                );
                                              setFieldValue(
                                                `routeInfos[${index}].travelDate`,
                                                formattedDate,
                                              );

                                              // Update any subsequent dates that are now invalid
                                              updateSubsequentDates(
                                                values,
                                                setFieldValue,
                                                index,
                                                newDate,
                                              );
                                            } else {
                                              const dateValue =
                                                index > 0
                                                  ? values.routeInfos[index]
                                                      .travelDate
                                                    ? new Date(
                                                        values.routeInfos[index]
                                                          .travelDate,
                                                      )
                                                    : values.routeInfos[
                                                          index - 1
                                                        ].travelDate
                                                      ? new Date(
                                                          values.routeInfos[
                                                            index - 1
                                                          ].travelDate,
                                                        )
                                                      : new Date()
                                                  : values.routeInfos[index]
                                                        .travelDate
                                                    ? new Date(
                                                        values.routeInfos[index]
                                                          .travelDate,
                                                      )
                                                    : new Date();
                                              const formattedDate =
                                                dayjs(dateValue).format(
                                                  "YYYY-MM-DD",
                                                );
                                              setFieldValue(
                                                `routeInfos[${index}].travelDate`,
                                                formattedDate,
                                              );
                                            }

                                            // Call the multicity date selected callback
                                            handleMulticityDateSelected(
                                              index,
                                              values,
                                            );
                                          }}
                                          disabled={{
                                            before:
                                              index > 0 &&
                                              values.routeInfos[index - 1]
                                                ?.travelDate
                                                ? new Date(
                                                    values.routeInfos[index - 1]
                                                      .travelDate,
                                                  )
                                                : new Date(),
                                          }}
                                          initialFocus
                                          className="text-black"
                                        />
                                      </PopoverContent>
                                    </Popover>
                                  </div>
                                  <div className="flex items-center relative my-3 ">
                                    <div className="absolute left-[90%] ml-2 md:mt-[0px] mt-7">
                                      {index > 1 && (
                                        <button
                                          type="button"
                                          className="text-primary rounded-full bg-transparent w-6 h-6 leading-6 flex items-center justify-center text-[20px] "
                                          onClick={() => {
                                            remove(index);
                                            const newDateRanges = [
                                              ...dateRanges,
                                            ];
                                            newDateRanges.splice(index, 1);
                                            setDateRanges(newDateRanges);
                                          }}
                                        >
                                          <CircleMinus />
                                        </button>
                                      )}
                                    </div>
                                    <div className="absolute md:w-[200px] md:left-10 md:mt-[0px] mt-7">
                                      {index === values.routeInfos.length - 1 &&
                                        values.routeInfos.length < 5 && (
                                          <button
                                            type="button"
                                            className="p-2 bg-transparent border-[2px] text-black rounded"
                                            onClick={() => {
                                              push({
                                                fromCityOrAirport: { code: "" },
                                                toCityOrAirport: { code: "" },
                                                travelDate:
                                                  values.routeInfos[index]
                                                    .travelDate ||
                                                  dayjs().format("YYYY-MM-DD"),
                                              });
                                              setDateRanges([
                                                ...dateRanges,
                                                undefined,
                                              ]);

                                              // Update selectedCityNames to preserve existing entries and add new empty one
                                              setSelectedCityNames([
                                                ...selectedCityNames,
                                                {
                                                  fromCityOrAirport: "",
                                                  toCityOrAirport: "",
                                                  index:
                                                    values.routeInfos.length,
                                                },
                                              ]);
                                            }}
                                          >
                                            Add One More
                                          </button>
                                        )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </>
                          )}
                        </FieldArray>
                      </TabsContent>

                      <PassengerClass
                        levels={{
                          title: "Service class",
                          data: [
                            { id: 0, title: "Economy" },
                            { id: 1, title: "Premium_Economy" },
                            { id: 2, title: "Business" },
                            { id: 3, title: "First" },
                          ],
                        }}
                        peoples={{
                          title: "Number of passengers",
                          formats: ["passenger", "passengers", "passengers"],
                          data: [
                            {
                              id: 0,
                              title: "Adults",
                              subtitle: "over 12 years old",
                              range: { min: 1, max: 9 },
                            },
                            {
                              id: 1,
                              title: "Children",
                              subtitle: "from 2 to 12 years",
                              range: { min: 0, max: 8 },
                            },
                            {
                              id: 2,
                              title: "Infants",
                              subtitle: "up to 2 years, no place",
                              range: { min: 0, max: 8 },
                            },
                          ],
                        }}
                        placeholder=""
                        defaultValue={{
                          ADULT: Number(initialValues.paxInfo.ADULT),
                          CHILD: Number(initialValues.paxInfo.CHILD),
                          INFANT: Number(initialValues.paxInfo.INFANT),
                        }}
                        defaultLevel={
                          initialValues.cabinClass[0] +
                          initialValues.cabinClass.slice(1).toLowerCase()
                        }
                        maxLength={9}
                        setFieldValue={setFieldValue}
                        shouldOpen={shouldOpenPassenger}
                      />

                      <Button
                        type="submit"
                        style={{ color: "white" }}
                        className="relative rounded w-[100%] lg:w-[20%] px-5 py-2.5 overflow-hidden group !bg-primary !hover:bg-gradient-to-r hover:theme_color hover:theme_color text-white !h-[58px]"
                        disabled={!validateForm(values)}
                      >
                        {isLoading && (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        )}
                        <span className="absolute -left-[90px] w-8 h-32 -mt-12 transition-all duration-1000 transform translate-x-12 bg-white opacity-10 rotate-12 group-hover:translate-x-64 ease"></span>
                        <span className="relative">Search Flights</span>
                      </Button>
                    </div>
                    <div>
                      <FlightSearchForms initialValues={initialValues} />
                    </div>
                  </div>
                </Tabs>
              </div>
            </Form>
          );
        }}
      </Formik>
      {searchHistory && searchHistory.length > 0 ? (
        <div className="relative">
          {/* Add carousel navigation buttons */}
          <button
            className="absolute left-0 2xl:-left-18 top-1/2 -translate-y-1/2 z-50 text-gray-500 bg-white/80 p-2 rounded-full shadow-md hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => {
              const container = document.getElementById("search-carousel");
              if (container) {
                container.scrollLeft -= container.offsetWidth;
              }
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>

          <button
            className="absolute right-0 2xl:-right-18 top-1/2 -translate-y-1/2 z-50 bg-white/80 text-gray-500 p-2 rounded-full shadow-md hover:bg-white disabled:cursor-not-allowed"
            onClick={() => {
              const container = document.getElementById("search-carousel");
              if (container) {
                container.scrollLeft += container.offsetWidth;
              }
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>

          {/* Carousel container */}
          <div
            id="search-carousel"
            className="flex overflow-x-auto gap-4 scroll-smooth scrollbar-hide snap-x snap-mandatory py-4 relative"
            style={{
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              WebkitOverflowScrolling: "touch",
            }}
          >
            {searchHistory?.map((item: any, index: number) => {
              const searchString = item?.searchString;
              const { metadata, ...rest } = JSON.parse(searchString);
              const routeInfos = rest?.routeInfos?.[0] || [];
              const urlString = new URLSearchParams({
                data: rest ? JSON.stringify(rest) : "",
                metadata: metadata ? JSON.stringify(metadata) : "",
                tripType: (item?.tripType || "oneway").toLowerCase(),
                _t: new Date().getTime().toString(),
              }).toString();

              return (
                <div key={index} className="flex-none w-[300px] snap-start">
                  <div
                    onClick={() => {
                      router.push(`/flight-list?${urlString}`);
                      logFirebaseEvent("AfterSearch_Flight");
                    }}
                    className="p-4 border rounded-lg hover:shadow-md transition-shadow cursor-pointer bg-white h-full"
                  >
                    <div>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2 text-gray-500">
                          <span className="font-semibold">
                            {routeInfos?.fromCityOrAirport?.code}
                          </span>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M5 12h14m-7-7l7 7-7 7" />
                          </svg>
                          <span className="font-semibold">
                            {routeInfos?.toCityOrAirport?.code}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-sm text-gray-500">
                          {routeInfos?.travelDate}
                        </span>
                        <span className="px-2 py-1 text-xs rounded bg-orange-100 text-orange-600">
                          {item?.tripType}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="text-center text-gray-500">
          No recent searches found.
        </div>
      )}
    </>
  );
};

export default FlightSearchForm;
